import { z } from "zod";
import { shareCardKind, shareChannel, shareFormat, shareRef } from "./share";
import { INTAKE_STEPS } from "./training/intake";
/**
 * What the app reports for product analytics (`D238`).
 *
 * ── THE APP REPORTS, THE SERVER EMITS ──────────────────────────────────────
 * Every analytics event leaves a Worker, never a device: there is no
 * analytics SDK in the bundle and nothing on the phone talks to the stream.
 * Most events are facts the server already sees (a profile write, a training
 * config's first write, a trainer turn). What it cannot see is the intake
 * moving forward, because `D126` writes nothing until the end, and a share
 * card offered or sent, which happens on the device alone (`D258`, `D260`) —
 * so the app says what happened here, the API stamps the account from the
 * access token, checks the opt-out, and emits.
 *
 * ── A CLOSED UNION, STRICT ─────────────────────────────────────────────────
 * A member is added here before the app sends it (inputs are strict: an
 * unknown event is a 422, which the app ignores — the call is
 * fire-and-forget and never retried, never queued in the outbox). Nothing in
 * the body names an account; the subject is the token's.
 *
 * `204` on success, and on an opted-out account too: the device is not told
 * who has opted out, and has nothing to do differently either way.
 */
export const PRODUCT_EVENTS_ROUTE = "/me/events";
/** The client bundle's version, at most this long (`CLIENT_ERROR_LIMITS`'s number). */
export const PRODUCT_EVENT_APP_VERSION_MAX = 64;
/** The bundle (`VITE_BUNDLE_VERSION`) — the `app_version` column. */
const appVersion = z.string().min(1).max(PRODUCT_EVENT_APP_VERSION_MAX);
/** The growth prompts that offer a club invite unasked (`D279`). */
export const CLUB_INVITE_TRIGGERS = ["empty", "start_five"];
const clubInviteTrigger = z.enum(CLUB_INVITE_TRIGGERS);
export const productEventInput = z.discriminatedUnion("event", [
    z.strictObject({
        appVersion: appVersion.optional(),
        event: z.literal("onboarding_step_completed"),
        /** An intake step this server knows — a newer app's step is a 422. */
        step: z.enum(INTAKE_STEPS),
    }),
    z.strictObject({
        appVersion: appVersion.optional(),
        event: z.literal("share_offered"),
        /** The card the app put in front of the person unasked (`D260`). An
         *  explicit share button offers nothing and reports nothing here. */
        kind: shareCardKind,
    }),
    z.strictObject({
        appVersion: appVersion.optional(),
        /** What the platform said about the target (`shareChannel`). */
        channel: shareChannel,
        event: z.literal("share_completed"),
        format: shareFormat,
        kind: shareCardKind,
        /** The card's link id — present only while links are on (`D257`). */
        ref: shareRef.optional(),
    }),
    z.strictObject({
        appVersion: appVersion.optional(),
        event: z.literal("club_invite_offered"),
        /** Which growth prompt put the invite in front of the person (`D279`):
         *  the club with only its creator, or "comece com 5 amigos". */
        trigger: clubInviteTrigger,
    }),
    z.strictObject({
        appVersion: appVersion.optional(),
        channel: shareChannel,
        event: z.literal("club_invite_shared"),
        /** Whether the invite carried a link, i.e. links were on (`D257`). */
        linked: z.boolean(),
        /** `menu` is the club's own "Convidar", asked for, not a prompt. */
        trigger: z.enum([...CLUB_INVITE_TRIGGERS, "menu"]),
    }),
]);
//# sourceMappingURL=product-events.js.map